import {
	Heading,
	Text,
	Box,
	Button,
	Table,
	AsyncSelect,
	Time,
	Inline,
	Icons,
	Tabs,
	TabsList,
	TabContent,
	Stack,
	joinAttributes,
	RelativeTime,
	Dropdown,
	Component,
	Divider,
	Badge,
	Spinner,
	useBreakpoints,
	Select,
	CheckboxInput,
	RadioInput,
	TimeDuration,
	Money,
} from "@sembark-travel/ui-base"
import { ButtonLink, Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	ListView,
	areAdvancedFiltersAppliedDefault,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { Omit, Optional } from "utility-types"
import React, {
	Fragment,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from "react"
import { ComposeEmail } from "../MailMessages"
import { WhatsAppLinkedConversations } from "../WhatsAppMessages"
import { SelectUsers } from "../Users"
import { ITripPlanRequest, LeadStatusType, TripPlanRequestXHR } from "./store"
import useSWR from "swr"
import { PhoneNumber, Email } from "../Contacts"
import { useAuthUser, useCheckPermissions, PERMISSIONS, IUser } from "../Auth"
import {
	Form,
	withServerErrors,
	validateFormValues,
	SubmissionError,
	SelectField,
	parsePhoneNumber,
	DatePickerField,
	SwitchInputField,
	useFieldValue,
	TextAreaInputField,
	TextInputField,
	RadioInputField,
} from "@sembark-travel/ui-form"
import arrayMutators from "final-form-arrays"
import * as Validator from "yup"
import { AddNewTrip, ITrip } from "../Trips"
import {
	dateToQuery,
	dateToUTCString,
	duration,
	endOf,
	fromNow,
	parseDate,
	parseDateFromQuery,
	startOf,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import { generatePath, useNavigate } from "../router-utils"
import { ITag, SelectTags, TagBadges } from "../Tags"
import { TemporaryStorage } from "../DownloadManager"
import config from "../config"
import {
	CommentableCommentsList,
	WhatsAppReminderFields,
	whatsAppReminderPayload,
	type TWhatsAppReminderValues,
} from "../Comments"
import { checkFilterAppliedFn } from "../utils"
import { PastInteractionsInDialog } from "./PastInteractions"

type TFilters = TSearchParams & {
	source?: string
	tags?: Array<ITag>
	owners?: Array<IUser>
	created_after?: Date
	created_before?: Date
	unassigned_only?: boolean
	ai_only?: boolean
	destinations?: Array<{ id: string; name: string }>
	leadStatus?: Array<{ name: string; id: string }>
	trip_sources?: Array<{ name: string; id: string }>
}

type TFiltersInQuery = TSearchParams & {
	source: "all" | "archived_only" | "new_only" | string
	tags?: Array<string>
	owners?: Array<string>
	ca?: string
	cb?: string
	unassigned_only?: 1
	ai_only?: 1
	destinations?: Array<string>
	leadStatus?: Array<string>
	trip_sources?: Array<string>
}

interface IEditTagsAndFollowUpsFormValues extends TWhatsAppReminderValues {
	tags?: Array<ITag>
	due_at?: Date
	note?: string
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		source,
		tags,
		q,
		page,
		created_after,
		created_before,
		unassigned_only,
		ai_only,
		owners,
		destinations,
		leadStatus,
		trip_sources,
	} = filters
	const query: TFiltersInQuery = { source: source || "new_only" }
	if (tags?.length) {
		query.tags = tags.map((t) => `${t.id}-${t.name}`)
	}
	if (owners?.length) {
		query.owners = owners.map((t) => `${t.id}-${t.name}`)
	}
	if (destinations?.length) {
		query.destinations = destinations.map((t) => `${t.id}-${t.name}`)
	}
	if (leadStatus?.length) {
		query.leadStatus = leadStatus.map((t) => `${t.id}-${t.name}`)
	}
	if (trip_sources?.length) {
		query.trip_sources = trip_sources.map((v) => v.id)
	}
	if (q?.length) {
		query.q = q
	}
	if (page) {
		query.page = page
	}
	if (created_after) {
		query.ca = dateToQuery(created_after)
	}
	if (created_before) {
		query.cb = dateToQuery(created_before)
	}
	if (unassigned_only) query.unassigned_only = 1
	if (ai_only) query.ai_only = 1
	return query
}

function queryToFilter(query: TFiltersInQuery): TFilters {
	const {
		source,
		tags,
		q,
		page,
		ca,
		cb,
		unassigned_only,
		ai_only,
		owners,
		destinations,
		leadStatus,
		trip_sources,
	} = query
	const filters: TFilters = { source: source || "new_only" }
	if (tags?.length) {
		filters.tags = tags.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as ITag
		})
	}
	if (owners?.length) {
		filters.owners = owners.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as IUser
		})
	}
	if (destinations?.length) {
		filters.destinations = destinations.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { id: string; name: string }
		})
	}
	if (leadStatus?.length) {
		filters.leadStatus = leadStatus.map((t) => {
			const [id, ...name] = t.split("-")
			return {
				id,
				name: name.join("-"),
			} as never as { name: string; id: string }
		})
	}
	if (trip_sources?.length) {
		filters.trip_sources = trip_sources.map((v) => {
			return { name: v, id: v }
		})
	}

	if (q?.length) {
		filters.q = q
	}
	if (page) {
		filters.page = page
	}
	if (ca) {
		filters.created_after = parseDateFromQuery(ca)
	}
	if (cb) {
		filters.created_before = parseDateFromQuery(cb)
	}
	if (unassigned_only) filters.unassigned_only = true
	if (ai_only) filters.ai_only = true
	return filters
}

export function TripPlanRequestsList() {
	const xhr = useXHR()
	const [refreshKey, setRefreshKey] = useState(1)
	const { hasPermission } = useCheckPermissions()
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilter,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		source: query.source || "new_only",
	})
	const canAssignTeam = hasPermission(PERMISSIONS.MANAGE_TRIP_PLAN_REQUESTS)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const { data: tripPlanRequestSources } = useSWR(
		`trip-plan-request-sources`,
		() => TripPlanRequestXHR(xhr).getTripPlanRequestSources()
	)
	const { xs } = useBreakpoints()

	const requestPayload = useMemo(() => {
		const {
			source,
			tags,
			owners,
			leadStatus,
			created_after,
			created_before,
			destinations,
			trip_sources,
			...other
		} = params || {}

		return {
			...other,
			trip_sources:
				source === "archived_only" || source === "new_only"
					? ["all"]
					: source
						? source === "all" && trip_sources?.length
							? trip_sources.map((v) => v.id)
							: [source]
						: null,
			archived_only: source === "archived_only" ? 1 : null,
			new_only: source === "new_only" ? 1 : null,
			tags: tags?.map((t) => t.id),
			owners: owners?.map((t) => t.id),
			destinations: destinations?.map((t) => t.id),
			lead_status: leadStatus?.map((t) => t.id).join(","),
			created_after: created_after
				? dateToUTCString(startOf(created_after, "day"))
				: null,
			created_before: created_before
				? dateToUTCString(endOf(created_before, "day"))
				: null,
		}
	}, [params])
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, page: 1 })
				}}
				title="Trip Plan Requests"
				resetParams={({ source }) => ({
					source,
					page: 1,
				})}
				areAdvancedFiltersApplied={(params) => {
					const { source, unassigned_only, ai_only, ...otherParams } = params
					return (
						areAdvancedFiltersAppliedDefault(otherParams) ||
						Boolean(unassigned_only) ||
						Boolean(ai_only)
					)
				}}
				actions={
					canAssignTeam ? (
						<Inline gap={"2"}>
							<ButtonLink
								to={generatePath(
									"/trip-plan-requests/auto-distribution-preferences"
								)}
								color="accent"
							>
								<Icons.Cog color="default" /> <Icons.NewBadge />
							</ButtonLink>
							<ButtonLink
								to={generatePath("/trip-plan-requests/report")}
								color="accent"
								level="primary"
								status="primary"
							>
								Report
							</ButtonLink>
						</Inline>
					) : null
				}
				Filters={Filters}
			>
				{({
					searchParams,
					setSearchParams,
					changeAdvancedFiltersVisibility,
				}) => (
					<Tabs>
						<TabsList>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="new_only"
								key="new_only"
							>
								New
							</TabLink>
							{tripPlanRequestSources?.map((a) => (
								<TabLink
									key={a.name}
									params={searchParams}
									setParams={setSearchParams}
									source={a.name}
								>
									<Box
										style={{ maxWidth: "100px" }}
										textOverflow="truncate"
										title={a.name}
									>
										{a.name}
									</Box>
								</TabLink>
							))}
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="all"
								key="all_all"
							>
								All
							</TabLink>
							<TabLink
								params={searchParams}
								setParams={setSearchParams}
								source="archived_only"
								key="archived_only"
							>
								Archived
							</TabLink>
						</TabsList>
						<TabContent style={{ minHeight: "50vh" }}>
							<ListView<ITripPlanRequest, TFilters>
								params={params}
								pageKey={`trip-plan-requests_${refreshKey}`}
								swrConfig={{ revalidateOnFocus: true }}
								fetch={(xhr) => {
									return TripPlanRequestXHR(xhr).getTripPlanRequests(
										requestPayload
									)
								}}
								onPageChange={(page) => setParams({ ...params, page })}
								actions={() => (
									<Dropdown alignRight>
										<Dropdown.ToggleButton>
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											<TemporaryStorage
												initUrl="/trip-plan-requests"
												initParams={{
													...requestPayload,
													_download: 1,
													timezone_offset: config.timezoneOffset,
												}}
											>
												{({ message, generate, url, reset }) =>
													url ? (
														<Dropdown.MenuItem
															as="a"
															href={url}
															target="_blank"
															download
															onClick={() => reset()}
														>
															{message}
														</Dropdown.MenuItem>
													) : (
														<Dropdown.MenuItem
															onClick={(e: React.MouseEvent) => {
																e.preventDefault()
																generate()
															}}
														>
															{message || (
																<>
																	<Icons.DocumentDownload /> Download as
																	CSV/Excel
																</>
															)}
														</Dropdown.MenuItem>
													)
												}
											</TemporaryStorage>
											<BulkAssignTripPlanRequest
												params={requestPayload}
												onSuccess={() => {
													setParams({ ...params })
													setRefreshKey((prev) => prev + 1)
												}}
												openAdvancedFilters={() => {
													changeAdvancedFiltersVisibility(true)
												}}
											>
												{({ onAssign }) => (
													<Dropdown.MenuItem onClick={onAssign}>
														<Icons.User /> Bulk Assign Leads{" "}
													</Dropdown.MenuItem>
												)}
											</BulkAssignTripPlanRequest>
										</Dropdown.Menu>
									</Dropdown>
								)}
							>
								{({ items, refresh }) =>
									xs ? (
										<Box>
											<Stack gap="2" as="ol">
												{items.map((r) => (
													<Box
														as="li"
														key={r.id}
														bgColor="default"
														padding="4"
														rounded="md"
														borderWidth="1"
													>
														<Box style={{ float: "right" }} paddingLeft="2">
															<Actions
																request={r}
																onChange={refresh}
																canAssignTeam={canAssignTeam}
															/>
														</Box>
														<Stack gap="2">
															<GuestDetails
																request={r}
																showFullDetailsInDialog
																showSource={
																	(params.source === "all" ||
																		params.source === "new_only") &&
																	Number(tripPlanRequestSources?.length || 0) >
																		1
																}
															/>
															{canAssignTeam ? (
																<Inline gap="1">
																	<Box>
																		<Icons.User
																			title="Team Member"
																			color="muted"
																		/>
																	</Box>
																	<Box flex="1" minWidth="0">
																		{r.owner ? (
																			<Stack>
																				{r.owner.name}
																				{r.assigned_at ? (
																					<Box>
																						<RelativeTime
																							fontSize="sm"
																							timestamp={r.assigned_at}
																							color="muted"
																						/>
																					</Box>
																				) : null}
																			</Stack>
																		) : (
																			<Text color="muted">Unassigned</Text>
																		)}
																	</Box>
																</Inline>
															) : null}
															<Stack gap="2">
																{r.tags?.length || r.notes?.length ? (
																	<>
																		<TagBadges tags={r.tags} />

																		{/* Follow up notes and comments are same so using same component */}
																		<CommentableCommentsList
																			comments={r.notes || []}
																			onChange={refresh}
																		/>
																	</>
																) : (
																	<Text color="muted">-</Text>
																)}
																{r.deleted_at ? (
																	<Text color="warning">
																		<Icons.Trash title="Archived" />{" "}
																		{r.deleted_by?.name || "N/A"},{" "}
																		<RelativeTime timestamp={r.deleted_at} />
																	</Text>
																) : null}
															</Stack>
														</Stack>
													</Box>
												))}
											</Stack>
										</Box>
									) : (
										<Table
											headers={[
												"Contact",
												"Travel Details",
												"Tags and Follow-Ups",
											].concat(canAssignTeam ? ["Team", ""] : [""])}
											alignCols={{ 3: "right", 4: "right" }}
											hover
											bordered
											rowKeys={items.map((r) => r.id)}
											responsive
											rows={items.map((r) =>
												[
													<GuestDetails
														request={r}
														showFullDetailsInDialog
														showSource={
															(params.source === "all" ||
																params.source === "new_only") &&
															Number(tripPlanRequestSources?.length || 0) > 1
														}
													/>,
													<Stack gap="px" maxWidth="md" wordBreak="words">
														{r.destination ? (
															<Text fontWeight="semibold">{r.destination}</Text>
														) : null}
														<Inline gap="px">
															{joinAttributes(
																r.start_date ? (
																	<Time
																		value={parseDate(
																			r.start_date,
																			"YYYY-MM-DD"
																		)}
																	/>
																) : null,
																r.no_of_days
																	? `${r.no_of_days - 1}N, ${r.no_of_days}D`
																	: null
															)}
														</Inline>
														{r.hotel_preference ? (
															<Text maxWidth="xs" fontSize="sm">
																{r.hotel_preference}
															</Text>
														) : null}

														{r.extra_fields ? (
															<Stack>
																{Object.keys(r.extra_fields).map((key) => (
																	<Text key={key}>
																		{key}:{" "}
																		{
																			JSON.stringify(
																				(r.extra_fields?.[key] as never) || ""
																			) as string
																		}
																	</Text>
																))}
															</Stack>
														) : null}
														{r.comments ? (
															<Text>comment: {r.comments}</Text>
														) : null}
													</Stack>,
													<Stack gap="2">
														{r.tags?.length || r.notes?.length ? (
															<>
																<TagBadges tags={r.tags} />

																<CommentableCommentsList
																	comments={r.notes || []}
																	onChange={refresh}
																/>
															</>
														) : (
															<Text color="muted">-</Text>
														)}

														{/* Follow up notes and comments are same so using same component */}
														{r.deleted_at ? (
															<Text color="warning">
																<Icons.Trash title="Archived" />{" "}
																{r.deleted_by?.name || "N/A"},{" "}
																<RelativeTime timestamp={r.deleted_at} />
															</Text>
														) : null}
													</Stack>,
												]
													.concat(
														canAssignTeam
															? [
																	<Box>
																		<Box>
																			{r.owner ? (
																				<Stack gap="1">
																					{r.owner.name}
																					{r.assigned_at ? (
																						<Box>
																							<RelativeTime
																								timestamp={r.assigned_at}
																							/>
																						</Box>
																					) : null}
																				</Stack>
																			) : (
																				<Box>-</Box>
																			)}
																		</Box>
																	</Box>,
																]
															: []
													)
													.concat([
														<Actions
															request={r}
															onChange={refresh}
															canAssignTeam={canAssignTeam}
														/>,
													])
											)}
										/>
									)
								}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Fragment>
	)
}

const PURPOSE_LABELS: Record<string, string> = {
	quotation_request: "Quotation",
	enquiry: "Enquiry",
	group: "Group",
	mice: "MICE",
	wedding: "Wedding",
	corporate: "Corporate",
	reconfirmation: "Reconfirmation",
	amendment: "Amendment",
	cancellation: "Cancellation",
	refund: "Refund",
	complaint: "Complaint",
	payment: "Payment",
}

function formatPurpose(value: string): string {
	return PURPOSE_LABELS[value] ?? value
}

function GuestDetails({
	request: r,
	showSource,
	showFullDetailsInDialog,
}: {
	request: ITripPlanRequest
	showSource?: boolean
	showFullDetailsInDialog?: boolean
}) {
	return (
		<Inline gap="2" flexWrap="wrap">
			<Stack gap="2" flex={"1"}>
				<Stack gap="1">
					<Inline gap="2" flexWrap="wrap">
						{showSource ? (
							<Inline alignItems="center" color="muted" gap="1" fontSize="sm">
								<Icons.ChevronDown rotate="270" title="Source" />
								<Text>{r.trip_source}</Text>
							</Inline>
						) : null}
						{r.is_ai_generated ? (
							<Badge
								status="primary"
								fullRounded
								title="Auto-created by the AI email parser"
							>
								<Icons.SparklesSolid size="3" /> AI
							</Badge>
						) : null}
						{typeof r.extra_fields?.purpose === "string" &&
						r.extra_fields.purpose &&
						r.extra_fields.purpose !== "quotation_request" &&
						r.extra_fields.purpose !== "enquiry" ? (
							<Badge
								fullRounded
								outlined
								title={`Parser query type: ${r.extra_fields.purpose}`}
							>
								{formatPurpose(r.extra_fields.purpose)}
							</Badge>
						) : null}
						<LinkedWhatsAppBadge request={r} />
					</Inline>
					<Inline gap="1" alignItems="baseline">
						<Component initialState={false}>
							{({ state, setState }) => (
								<>
									<a
										href="#view-details"
										onClick={(e) => {
											e.preventDefault()
											if (showFullDetailsInDialog) {
												setState(true)
											}
										}}
									>
										<Heading
											as="h4"
											fontSize="md"
											color={showFullDetailsInDialog ? "accent" : undefined}
										>
											{r.name}
										</Heading>
									</a>
									<Dialog
										open={state}
										onClose={() => setState(false)}
										title="Trip Plan Request Details"
									>
										<Dialog.Body>
											<TripPlanRequestDetails request={r} />
										</Dialog.Body>
										<Dialog.Footer>
											<Button onClick={() => setState(false)}>Ok</Button>
										</Dialog.Footer>
									</Dialog>
								</>
							)}
						</Component>
						{r.no_of_adults || r.no_of_adults ? (
							<Text fontWeight="semibold">
								(
								{[
									r.no_of_adults ? `${r.no_of_adults}A` : null,
									r.no_of_children ? `${r.no_of_children}C` : null,
								]
									.filter(Boolean)
									.join(" + ")}
								)
							</Text>
						) : null}
					</Inline>
				</Stack>
				<Stack gap="px">
					{r.phone_number ? (
						<Box>
							<PhoneNumber
								value={r.phone_number}
								callLoggableId={r.id}
								callLoggableType="trip_plan_requests"
								contactName={r.name}
							/>
						</Box>
					) : null}
					{r.email ? (
						<Box>
							<Email value={r.email} />
						</Box>
					) : null}
					{r.created_at && (
						<Text
							fontSize="base"
							color={"muted"}
							style={{ fontStyle: "italic" }}
						>
							- <RelativeTime timestamp={r.created_at} />
						</Text>
					)}
				</Stack>
			</Stack>
			{r.has_past_interactions?.calls || r.has_past_interactions?.mails ? (
				<PastInteractionsInDialog
					requestId={r.id}
					hasPastInteractions={r.has_past_interactions}
				>
					{({ open }) => (
						<Button level="secondary" onClick={open} status="primary">
							<Icons.Message />
						</Button>
					)}
				</PastInteractionsInDialog>
			) : null}
		</Inline>
	)
}

/**
 * The lead's WhatsApp messages across its linked conversations, counted like
 * its emails, opening the conversations in a dialog for a quick chat without
 * leaving the listing.
 */
function LinkedWhatsAppBadge({ request: r }: { request: ITripPlanRequest }) {
	const [open, setOpen] = useState(false)
	if (!r.wa_messages_count) {
		return null
	}
	return (
		<>
			<Badge fullRounded outlined status="success">
				<a
					href="#linked-whatsapp"
					onClick={(e) => {
						e.preventDefault()
						setOpen(true)
					}}
					title="WhatsApp messages"
				>
					<Icons.WhatsApp size="3" /> {r.wa_messages_count}
				</a>
			</Badge>
			<Dialog
				open={open}
				onClose={() => setOpen(false)}
				xl
				title="WhatsApp Conversations"
			>
				<Box style={{ height: "75vh", minHeight: 0 }}>
					{open ? (
						<WhatsAppLinkedConversations
							modelType="trip_plan_requests"
							modelId={Number(r.id)}
							emptyStateMessage="No WhatsApp conversations are linked to this lead yet."
						/>
					) : null}
				</Box>
			</Dialog>
		</>
	)
}

function Actions({
	request: r,
	onChange: refresh,
	canAssignTeam,
}: {
	request: ITripPlanRequest
	onChange: () => void
	canAssignTeam: boolean
}) {
	const xhr = useXHR()
	const navigate = useNavigate()
	const { user } = useAuthUser()
	const canSendEmail = Boolean(user?.reply_mailboxes_count) && !r.deleted_at

	return (
		<Inline justifyContent="end" alignItems="center" gap="2">
			{r.trip_id ? (
				<Link
					to={`/trips/${r.trip_id}`}
					color="accent"
					fontWeight="semibold"
					anchored
				>
					#&nbsp;{r.trip_id}
				</Link>
			) : !r.deleted_at ? (
				<CreateNewQueryFromRequestInDialog
					request={r}
					onSuccess={(trip) => {
						navigate("/trips/:tripId", {
							params: { tripId: trip.id.toString() },
						})
					}}
				>
					{({ create }) => (
						<Button
							title={
								getLinkedTripsForRequest(r).length > 0
									? "Attach to Linked Trip"
									: "Create New Query"
							}
							size="sm"
							onClick={create}
						>
							<Icons.Plus />
						</Button>
					)}
				</CreateNewQueryFromRequestInDialog>
			) : null}
			<Box>
				<Component initialState={false}>
					{({ state: isEditing, setState: changeIsEditing }) => (
						<>
							<Dialog
								open={isEditing}
								onClose={() => changeIsEditing(false)}
								title="Assign Team Member"
								sm
							>
								<Dialog.Body>
									<Form<{ owner: typeof r.owner }>
										initialValues={{
											owner: r.owner,
										}}
										validate={validateFormValues(
											Validator.object().shape({
												owner: Validator.object()
													.nullable()
													.typeError("Please select a user to assign."),
											})
										)}
										onSubmit={withServerErrors(async (values) => {
											const { owner } = values
											await TripPlanRequestXHR(xhr).assignTripRequestsOwner(
												[r.id],
												owner?.id
											)
											changeIsEditing(false)
											refresh()
											if (!owner) {
												showSnackbar("Team member remove from the request.")
											} else {
												showSnackbar("Team member assigned to the request.")
											}
										})}
										subscription={{
											submitting: true,
										}}
									>
										{({ submitting, handleSubmit }) => (
											<form noValidate onSubmit={handleSubmit}>
												<SelectField
													select={SelectUsers}
													name="owner"
													fetchOnMount
													multiple={false}
													label="Select Team Member"
												/>
												<Divider sm />
												<Stack gap="4">
													<SubmissionError />
													<Inline gap="4">
														<Button type="submit" disabled={submitting}>
															{submitting ? "Updating..." : "Assign"}
														</Button>
														<Button
															type="button"
															disabled={submitting}
															onClick={() => changeIsEditing(false)}
														>
															Cancel
														</Button>
													</Inline>
												</Stack>
											</form>
										)}
									</Form>
								</Dialog.Body>
							</Dialog>

							<EditTagsAndFollowUpsDialog request={r} onChange={refresh}>
								{({ onEdit: editTags }) => (
									<ComposeEmail
										title="Send Email"
										modelType="trip_plan_requests"
										modelId={Number(r.id)}
										onSuccess={refresh}
									>
										{({ onCompose }) => (
											<Dropdown alignRight>
												<Dropdown.ToggleButton level="tertiary" size="sm">
													<Icons.DotsVertical />
												</Dropdown.ToggleButton>
												<Dropdown.Menu>
													<Dropdown.MenuItem onClick={() => editTags()}>
														<Icons.Tags /> Edit Tags and Follow-Ups{" "}
														<Text as="span">
															<Icons.NewFeature size={"10"} />
														</Text>
													</Dropdown.MenuItem>
													{canSendEmail ? (
														<Dropdown.MenuItem
															onClick={(e) => {
																e.preventDefault()
																onCompose()
															}}
														>
															<Icons.Mail /> Send Email
														</Dropdown.MenuItem>
													) : null}
													{!r.trip_id ? (
														<>
															{canAssignTeam ? (
																<Dropdown.MenuItem
																	onClick={() => changeIsEditing(true)}
																>
																	<Icons.User /> Assign Team
																</Dropdown.MenuItem>
															) : null}
															<Dropdown.MenuItemDivider />
															{!r.deleted_at ? (
																<Dropdown.MenuItem
																	onClick={async () => {
																		if (
																			window.confirm(
																				"Are you sure you want to archive this plan request ? You can restore it any time."
																			)
																		) {
																			const { message } =
																				await TripPlanRequestXHR(
																					xhr
																				).archiveTripPlanRequest(r.id)
																			refresh()
																			showSnackbar(
																				message ||
																					"Request archived successfully"
																			)
																		}
																	}}
																>
																	<Icons.Trash color="danger" /> Archive Request
																</Dropdown.MenuItem>
															) : (
																<Dropdown.MenuItem
																	onClick={async () => {
																		if (
																			window.confirm(
																				"Are you sure you want to restore this archived trip plan request ?"
																			)
																		) {
																			const { message } =
																				await TripPlanRequestXHR(
																					xhr
																				).restoreTripPlanRequest(r.id)
																			refresh()
																			showSnackbar(
																				message ||
																					"Request restored successfully"
																			)
																		}
																	}}
																>
																	<Icons.Refresh color="success" /> Restore
																	Request
																</Dropdown.MenuItem>
															)}
														</>
													) : null}
												</Dropdown.Menu>
											</Dropdown>
										)}
									</ComposeEmail>
								)}
							</EditTagsAndFollowUpsDialog>
						</>
					)}
				</Component>
			</Box>
		</Inline>
	)
}

function Filters() {
	const { value: type } = useFieldValue<string | undefined>("source")
	const getLeadStatusType = useMemo(
		() =>
			type === "new_only"
				? LeadStatusType.filter(
						(status) =>
							status.id === "active_followups" ||
							status.id === "pending_followups"
					)
				: LeadStatusType,
		[type]
	)

	return (
		<Stack gap="4">
			<SelectField
				label="Tags"
				name="tags"
				select={SelectTags}
				type="trips"
				fetchOnMount
				multiple
				creatable={false}
			/>
			<Stack gap="1">
				<label htmlFor="field_created_after">Created During</label>
				<Box display="flex" alignItems="center">
					<DatePickerField name="created_after" dateFormat="DD MMM, YYYY" />
					<Box>
						<Icons.SwitchHorizontal />
					</Box>
					<DatePickerField
						name="created_before"
						rightAlign
						dateFormat="DD MMM, YYYY"
					/>
				</Box>
			</Stack>
			<SelectField
				label="Team Members"
				name="owners"
				select={SelectUsers}
				multiple
				withDeleted={1}
			/>
			<SelectField
				label="Destinations"
				name="destinations"
				select={SelectTripPlanRequestDestinations}
				multiple
			/>
			{type === "archived_only" ? null : (
				<SelectField
					select={Select}
					name="leadStatus"
					label="Status Type"
					options={getLeadStatusType}
					multiple
				/>
			)}
			{type !== "all" ? null : (
				<SelectField
					select={SelectLeadSources}
					name="trip_sources"
					label="Trip Sources"
					multiple
				/>
			)}

			<SwitchInputField label="Unassigned Team" name="unassigned_only" />
			<SwitchInputField label="AI Leads" name="ai_only" />
		</Stack>
	)
}

export function SelectTripPlanRequestDestinations(
	props: Optional<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			fetch={(q) =>
				TripPlanRequestXHR(xhr)
					.getTripPlanRequestDestinations({ q })
					.then((resp) => resp)
			}
			{...props}
		/>
	)
}

function TripPlanRequestDetails({ request }: { request: ITripPlanRequest }) {
	return (
		<Inline gap="6" flexWrap="wrap">
			<Stack gap="1">
				<Heading fontSize="base" color="primary">
					Guest Details
				</Heading>
				<GuestDetails request={request} showSource={false} />
			</Stack>
			<Stack gap="1">
				<Heading fontSize="base" color="primary">
					Destination
				</Heading>
				<Stack gap="px">
					{request.destination ? (
						<Text fontWeight="semibold" fontSize="md">
							{request.destination}
						</Text>
					) : null}
					<Inline gap="px">
						{joinAttributes(
							request.start_date ? (
								<Time value={parseDate(request.start_date, "YYYY-MM-DD")} />
							) : null,
							request.no_of_days
								? `${request.no_of_days - 1}N,${request.no_of_days}D`
								: null
						)}
					</Inline>
				</Stack>
			</Stack>
			<Stack gap="1" maxWidth="sm" wordBreak="words">
				<Heading fontSize="base" color="primary">
					More Details
				</Heading>
				<Stack gap="px">
					{request.comments ? (
						<Box maxWidth="xs" whiteSpace="preserveLine">
							{request.comments}
						</Box>
					) : null}
					{request.hotel_preference ? (
						<Text>{request.hotel_preference}</Text>
					) : null}
					{request.extra_fields ? (
						<Stack>
							{Object.keys(request.extra_fields).map((key) => (
								<Text key={key}>
									{key}:{" "}
									{
										JSON.stringify(
											(request.extra_fields?.[key] as never) || ""
										) as string
									}
								</Text>
							))}
						</Stack>
					) : null}
					<Box>
						<Badge success>{request.trip_source}</Badge>
					</Box>
				</Stack>
			</Stack>
		</Inline>
	)
}

function getLinkedTripsForRequest(request: ITripPlanRequest) {
	// Existing Trips reached via the lead's shared email thread(s) — e.g. an
	// amendment lead for an existing Trip. Skip the lead's own converted Trip.
	return (request.linked_mail_threaded_trips || []).filter(
		(t) => t.id !== request.trip_id
	)
}

export function CreateNewQueryFromRequestInDialog({
	children,
	request,
	onSuccess,
}: {
	request: ITripPlanRequest
	children: (props: { create: () => void }) => React.ReactNode
	onSuccess: (trip: Pick<ITrip, "id">) => void
}) {
	const [isOpenDialog, openDialog, closeDialog] = useDialog()
	const hasLinkedTrips = getLinkedTripsForRequest(request).length > 0
	return (
		<>
			{children({ create: openDialog })}
			<Dialog
				open={isOpenDialog}
				onClose={closeDialog}
				title={
					hasLinkedTrips ? "Attach to Existing Trip" : "Link or Create New Trip"
				}
				lg
			>
				<Dialog.Body>
					<TripPlanRequestDetails request={request} />
					<Divider />
					<CreateNewQueryFromRequest
						request={request}
						onSuccess={(trip) => {
							closeDialog()
							onSuccess(trip)
						}}
						onCancel={closeDialog}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function AttachRequestToLinkedTrip({
	request,
	trips,
	onSuccess,
	onCancel,
}: {
	request: ITripPlanRequest
	trips: NonNullable<ITripPlanRequest["linked_mail_threaded_trips"]>
	onSuccess: (trip: Pick<ITrip, "id">) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const [attachingId, setAttachingId] = useState<number | null>(null)
	return (
		<Stack gap="4">
			<Text>
				This request is already linked to the following trip(s) via email.
				Attach it to the same trip instead of creating a new one.
			</Text>
			<Stack gap="2">
				{trips.map((t) => (
					<Inline
						key={t.id}
						justifyContent="between"
						alignItems="center"
						gap="4"
						borderWidth="1"
						rounded="md"
						paddingX="4"
						paddingY="2"
					>
						<Stack gap="1">
							<Text fontWeight="semibold">Trip #{t.id}</Text>
							<Text fontSize="sm" color="muted">
								{t.current_status}
							</Text>
						</Stack>
						<Button
							status="primary"
							size="sm"
							disabled={attachingId !== null}
							onClick={async () => {
								setAttachingId(t.id)
								try {
									await TripPlanRequestXHR(xhr).attachTrip(request.id, {
										trip_id: t.id,
									})
									showSnackbar(
										`This request has been attached to Trip #${t.id}.`
									)
									onSuccess({ id: t.id })
								} catch (e) {
									alert((e as Error).message || "Something went wrong")
									setAttachingId(null)
								}
							}}
						>
							{attachingId === t.id ? "Attaching..." : "Attach"}
						</Button>
					</Inline>
				))}
			</Stack>
			<Inline>
				<Button
					level="tertiary"
					onClick={onCancel}
					disabled={attachingId !== null}
				>
					Cancel
				</Button>
			</Inline>
		</Stack>
	)
}

function CreateNewQueryFromRequest({
	request,
	onSuccess,
	onCancel,
}: {
	request: ITripPlanRequest
	onSuccess: (trip: Pick<ITrip, "id">) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const requestId = useId()
	const linkedTrips = getLinkedTripsForRequest(request)
	const [actionType, setActionType] = useState<"link" | "create">("create")
	const { data } = useSWR(
		// Skip the new-trip prefill when we're going to attach to an existing one.
		linkedTrips.length > 0
			? null
			: `trip-plan-request-to-trip/${request.id}?${requestId}`,
		() =>
			TripPlanRequestXHR(xhr)
				.getPayloadForTrip(request.id)
				.then((payload) => {
					const parsedPhoneNumbers = payload.tourist?.phone_numbers?.length
						? payload.tourist.phone_numbers.map((number) =>
								parsePhoneNumber(number, "IN")
							)
						: []
					setActionType(payload.suggested_trips?.length ? "link" : "create")
					return {
						reference_id: payload.reference_id || "",
						start_date: payload.start_date
							? startOf(parseDate(payload.start_date, "YYYY-MM-DD"), "day")
							: undefined,
						no_of_days: payload.no_of_days,
						no_of_nights: payload.no_of_nights,
						destinations: payload.destinations || [],
						no_of_adults: payload.no_of_adults,
						children: payload.children || [],
						trip_source: payload.trip_source || undefined,
						trip_source_contact: payload.trip_source_contact || undefined,
						comments: payload.comments || "",
						tourist: {
							name: payload.tourist?.name || "",
							salutation: payload.tourist?.salutation || undefined,
							email: payload.tourist?.email || "",
							phone_numbers: parsedPhoneNumbers
								.map((parsedPhoneNumber) =>
									parsedPhoneNumber?.nationalNumber
										? {
												is_primary: true,
												country_code: parsedPhoneNumber?.country,
												number: parsedPhoneNumber?.nationalNumber,
												phone_number: parsedPhoneNumber?.number,
											}
										: undefined
								)
								.filter((p): p is NonNullable<typeof p> => Boolean(p)),
						},
						suggested_trips: payload.suggested_trips || [],
						can_create_new: payload.can_create_new,
						tags: request.tags || [],
					}
				})
	)

	// Attach-only when the request is already linked to existing trip(s).
	if (linkedTrips.length > 0) {
		return (
			<AttachRequestToLinkedTrip
				request={request}
				trips={linkedTrips}
				onSuccess={onSuccess}
				onCancel={onCancel}
			/>
		)
	}

	if (!data) {
		return <Spinner padding="4" alignCenter />
	}

	return (
		<Stack gap="6">
			{data.suggested_trips.length !== 0 ? (
				<Inline gap="4">
					<Inline
						flex="1"
						gap="2"
						borderWidth="1"
						rounded="md"
						paddingY="2"
						paddingX="4"
						alignItems="center"
						borderColor={actionType === "link" ? "accent_emphasis" : "default"}
						bgColor={actionType === "link" ? "accent" : undefined}
						onClick={() => setActionType("link")}
						cursor="pointer"
					>
						<RadioInput
							name="actionType"
							value="link"
							checked={actionType === "link"}
							onChange={() => setActionType("link")}
						/>
						<Stack gap="1">
							<Text as="span" fontWeight="semibold">
								Link to Existing Trip
							</Text>
							<Text as="span" fontSize="sm" color="muted">
								Attach this request to an existing trip. The request will be
								linked to the selected trip.
							</Text>
						</Stack>
					</Inline>
					<Inline
						flex="1"
						gap="2"
						borderWidth="1"
						rounded="md"
						paddingY="2"
						paddingX="4"
						alignItems="center"
						borderColor={
							actionType === "create" ? "accent_emphasis" : "default"
						}
						bgColor={actionType === "create" ? "accent" : undefined}
						onClick={() => setActionType("create")}
						cursor="pointer"
					>
						<RadioInput
							name="actionType"
							value="create"
							checked={actionType === "create"}
							onChange={() => setActionType("create")}
						/>
						<Stack gap="1">
							<Text as="span" fontWeight="semibold">
								Create New Lead
							</Text>
							<Text as="span" fontSize="sm" color="muted">
								Create a new trip from this request. The request will be linked
								to the newly created trip.
							</Text>
						</Stack>
					</Inline>
				</Inline>
			) : null}

			{actionType === "link" ? (
				<AttachRequestToTripForm
					initialValues={{
						trip_id: data.suggested_trips.length
							? String(data.suggested_trips[0].id)
							: "",
					}}
					suggestedTrips={data.suggested_trips}
					onSubmit={async (tripId) => {
						await TripPlanRequestXHR(xhr).attachTrip(request.id, {
							trip_id: tripId,
						})
						showSnackbar(`This request has been attached to Trip #${tripId}.`)
						onSuccess({ id: tripId })
					}}
				/>
			) : null}

			{actionType === "create" ? (
				<AddNewTrip
					initialValues={data}
					onSuccess={async (trip) => {
						return TripPlanRequestXHR(xhr)
							.attachTrip(request.id, { trip_id: trip.id })
							.then(() => {
								onSuccess(trip)
								showSnackbar(
									`Query (ID: ${trip.id}) Initiated for this request.`
								)
							})
							.catch((e) => {
								alert(e.message || "Something went wrong")
							})
					}}
					onCancel={onCancel}
				/>
			) : null}
		</Stack>
	)
}

function AttachRequestToTripForm({
	suggestedTrips,
	onSubmit,
	initialValues,
}: {
	suggestedTrips: ITrip[]
	onSubmit: (tripId: number) => Promise<void>
	initialValues: { trip_id?: string }
}) {
	return (
		<Form<{ trip_id?: string }>
			initialValues={initialValues}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit(Number(values.trip_id))
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Inline
						bgColor={"primary"}
						borderWidth={"1"}
						rounded="md"
						paddingY={"4"}
						paddingX={"4"}
						borderColor={"primary_emphasis"}
						gap="2"
					>
						<Box>
							<Icons.Info size={"6"} color={"primary"} />
						</Box>
						<Stack width={"full"} gap="4">
							<Stack gap="1">
								<Text as="h2" color={"primary"} fontWeight="semibold">
									Existing Trip Found
								</Text>
								<Text as="span" fontSize={"sm"} color="primary">
									You can attach this request to an existing trip.
								</Text>
							</Stack>
							<Stack width={"full"}>
								<Table
									bordered
									striped
									responsive
									headers={[
										"",
										"Trip ID",
										"Guest / B2B",
										"Source",
										"Details",
										"Quotes",
										"Team Members",
									]}
									rows={suggestedTrips.map(
										({
											id,
											tourist,
											no_of_adults,
											no_of_children,
											current_status,
											trip_source,
											trip_source_contact,
											reference_id,
											start_date,
											destinations,
											days,
											nights,
											team,
											created_at,
											given_quotes_count,
											latest_given_quote,
										}) => [
											<RadioInputField
												name="trip_id"
												value={String(id)}
												cursor="pointer"
											/>,
											<Stack gap="1">
												<Link
													anchored
													to={`/trips/${id}`}
													color={"accent"}
													fontWeight="semibold"
												>
													{id}
												</Link>
												<Badge
													status={
														current_status === "Converted"
															? "success"
															: undefined
													}
												>
													{current_status}
												</Badge>
											</Stack>,
											tourist ? (
												<Stack gap="px">
													<Text fontWeight="semibold">{tourist.name}</Text>
													<Box fontSize="sm" color="muted">
														{joinAttributes(
															<Text as="span">
																{no_of_adults}A
																{no_of_children ? `, ${no_of_children}C` : null}
															</Text>,
															[
																tourist.phone_numbers?.length,
																<PhoneNumber
																	value={tourist.phone_numbers}
																	iconOnly
																/>,
															],
															[
																tourist.email,
																<Email value={tourist.email} iconOnly />,
															]
														)}
													</Box>
												</Stack>
											) : null,
											<Stack gap="px">
												<Text fontWeight="semibold">
													{trip_source.short_name}
												</Text>
												{reference_id ? (
													<Text fontSize="sm">Ref# {reference_id}</Text>
												) : null}
												{trip_source_contact ? (
													<Inline
														alignItems="center"
														flexWrap="wrap"
														gap="2"
														fontSize="sm"
													>
														<Box color="muted">{trip_source_contact.name}</Box>
														<Box display="inlineBlock">
															{joinAttributes([
																trip_source_contact.phone_numbers?.length,
																<PhoneNumber
																	value={trip_source_contact.phone_numbers}
																	iconOnly
																/>,
															])}
														</Box>
													</Inline>
												) : null}
											</Stack>,
											<Stack gap="px">
												<Text
													style={{ maxWidth: "140px" }}
													textOverflow="truncate"
												>
													{destinations.map((d) => d.name).join(", ")}
												</Text>
												<Box fontSize="sm" color="muted">
													{joinAttributes(
														<Time timestamp={start_date} />,
														<TimeDuration value={duration(days, "days")}>
															{nights}N
														</TimeDuration>
													)}
												</Box>
											</Stack>,
											latest_given_quote ? (
												<Box
													title={`Latest quote ${fromNow(
														utcTimestampToLocalDate(
															latest_given_quote.created_at
														)
													)}`}
												>
													<Text>{given_quotes_count} Q</Text>
													<Money
														amount={latest_given_quote.given_price}
														currency={latest_given_quote.given_currency}
														color="muted"
														fontSize="sm"
													/>
												</Box>
											) : (
												"-"
											),
											<Stack gap="px">
												{team?.length ? (
													<Stack style={{ width: "80px" }} gap="px">
														{team.map((t) => (
															<Text
																textOverflow="truncate"
																key={t.id}
																fontSize="sm"
																color="muted"
															>
																{t.name}
															</Text>
														))}
													</Stack>
												) : null}
												<RelativeTime fontSize="sm" timestamp={created_at} />
											</Stack>,
										]
									)}
								/>
							</Stack>
							<SubmissionError />
							<Inline gap="4" alignItems={"center"}>
								<Button type="submit" level="primary" disabled={submitting}>
									{submitting ? "Attaching..." : "Attach to Trip"}
								</Button>
								<Text color={"muted"}>
									{suggestedTrips.length} result
									{suggestedTrips.length > 1 ? "s" : ""}
								</Text>
							</Inline>
						</Stack>
					</Inline>
				</form>
			)}
		</Form>
	)
}

export function SelectTripPlanRequests(
	props: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			fetch={(q) =>
				TripPlanRequestXHR(xhr)
					.getTripPlanRequests({ q })
					.then((resp) => resp.data)
			}
			{...props}
		/>
	)
}

function TabLink({
	params,
	setParams,
	children,
	source,
}: {
	params: TFilters
	setParams: (params: TFilters) => void
	children: React.ReactNode
	showCount?: boolean
	extraParams?: unknown
	source?: string
}) {
	return (
		<li className={(source ? params.source === source : true) ? "active" : ""}>
			<a
				href={`#${source}`}
				onClick={(e) => {
					e.preventDefault()
					const newParams = { ...params }
					if (source) {
						newParams.source = source
					}
					setParams(newParams)
				}}
			>
				{children}
			</a>
		</li>
	)
}

export function SelectLeadSources({
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	const { data } = useSWR(`/trip-plan-request-sources`, () =>
		TripPlanRequestXHR(xhr).getTripPlanRequestSources()
	)
	return <Select {...props} options={data || []} />
}

const EditTagsAndFollowUpsValidation = validateFormValues(
	Validator.object().shape({
		tags: Validator.array().of(
			Validator.object().shape({
				name: Validator.string().required(),
			})
		),
		due_at: Validator.date()
			.nullable()
			// The WhatsApp message is sent at the due time, so without one
			// there is no moment at which it would go out.
			.when("wa_template", {
				is: (value: unknown) => Boolean(value),
				then: (schema) =>
					schema.required(
						"Due date is required when a WhatsApp message is attached"
					),
			}),
		note: Validator.string()
			.trim()
			.min(3, "Follow up Note must be at least 3 characters")
			.max(500, "Follow up Note cannot be more than 500 characters")
			.when("due_at", {
				is: (due_at: Date) => due_at,
				then: (schema) =>
					schema.required("Follow up note is required when due date is set"),
				otherwise: (schema) => schema.nullable(),
			}),
	})
)

function EditTagsAndFollowUpsDialog({
	children,
	request,
	onChange,
}: {
	children: (props: { onEdit: () => void }) => React.ReactNode
	request: ITripPlanRequest
	onChange: () => void
}) {
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ onEdit: open })}
			<Dialog open={isOpen} onClose={close} title="Edit Tags and Follow-Ups" sm>
				<Dialog.Body>
					<EditTagsAndFollowUpsForm
						request={request}
						onCancel={close}
						onChange={() => {
							close()
							onChange()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function EditTagsAndFollowUpsForm({
	request,
	onCancel,
	onChange,
}: {
	request: ITripPlanRequest
	onChange: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	return (
		<Form<IEditTagsAndFollowUpsFormValues>
			initialValues={{
				tags:
					request.tags?.map((t) => ({
						id: t.id,
						name: t.name,
						description: t.description,
					})) || [],
			}}
			validate={EditTagsAndFollowUpsValidation}
			onSubmit={withServerErrors(async (values) => {
				await TripPlanRequestXHR(xhr).editTagsAndFollowUps(request.id, {
					note: values.note,
					tags: values.tags?.map((t) => t.name),
					due_at: values.due_at ? dateToUTCString(values.due_at) : undefined,
					...whatsAppReminderPayload(values),
				})
				showSnackbar("Tags and Follow-Ups updated successfully")
				onChange()
			})}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<SelectField
							select={SelectTags}
							name="tags"
							fetchOnMount
							label="Select tags"
							type={"trips"}
							multiple
							creatable={false}
						/>

						<Inline gap="2" width="full" flexWrap={"wrap"}>
							<Box flex="1" minWidth="max">
								<TextAreaInputField
									label="Follow Up Note"
									name="note"
									placeholder="Enter follow up note"
									rows={3}
									help="Maximum of 500 characters"
									width="full"
								/>
							</Box>

							<DatePickerField
								label="Due Date"
								name="due_at"
								dateFormat="MMMM D, YYYY"
								help="If it needs to be resolved at a specific time"
								timeFormat
							/>
						</Inline>

						<WhatsAppReminderFields
							commentableType="trip_plan_requests"
							commentableId={request.id}
						/>

						<Stack gap="4">
							<SubmissionError />
							<Inline gap="4">
								<Button disabled={submitting} type="submit">
									{submitting ? "Saving..." : "Save"}
								</Button>
								{onCancel ? (
									<Button onClick={onCancel} disabled={submitting}>
										Cancel
									</Button>
								) : null}
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function BulkAssignTripPlanRequest({
	children,
	params,
	onSuccess,
	openAdvancedFilters,
}: {
	children: (props: { onAssign: () => void }) => React.ReactNode
	params: Record<string, unknown>
	onSuccess: () => void
	openAdvancedFilters: () => void
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()
	const [items, setItems] = useState<ITripPlanRequest[]>([])
	const [selectedIds, setSelectedIds] = useState<number[]>([])
	const [isSelectAll, setIsSelectAll] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [totalCount, setTotalCount] = useState(0)
	const [currentPage, setCurrentPage] = useState(1)
	const [batchId, setBatchId] = useState<string | null>(null)
	const hasMore = useRef(false)

	const areFiltersApplied = useMemo(
		() =>
			checkFilterAppliedFn(
				params,
				"page",
				"trip_sources",
				"source",
				"new_only",
				"archived_only"
			),
		[params]
	)

	const selectedCount = isSelectAll
		? totalCount - selectedIds.length
		: selectedIds.length

	const fetchRequests = useCallback(
		async (page: number, reset?: boolean) => {
			setIsLoading(true)
			try {
				const resp = await TripPlanRequestXHR(xhr).getTripPlanRequests({
					...params,
					page,
					limit: 20,
				})
				setItems((prev) => (reset ? resp.data : [...prev, ...resp.data]))
				setTotalCount(resp.meta?.total || resp.data.length)
				hasMore.current = Boolean(
					resp.meta?.last_page && resp.meta.last_page > page
				)
				setCurrentPage(page)
			} catch (e) {
				console.error(e)
			} finally {
				setIsLoading(false)
			}
		},
		[xhr, params]
	)

	useEffect(() => {
		if (isOpen && areFiltersApplied) {
			fetchRequests(1, true)
		}
		if (!isOpen) {
			setItems([])
			setSelectedIds([])
			setIsSelectAll(false)
			setTotalCount(0)
			setCurrentPage(1)
		}
	}, [isOpen, areFiltersApplied, fetchRequests])

	useEffect(() => {
		if (!batchId) return

		let isMounted = true
		showSnackbar("Assigning Trips...")

		const intervalId = window.setInterval(async () => {
			try {
				const resp = await xhr.get<{ data: { finishedAt: string | null } }>(
					`batches/${batchId}`
				)
				if (resp.data.data.finishedAt) {
					clearInterval(intervalId)
					if (isMounted) {
						showSnackbar("Trip plan requests assigned successfully!")
						setBatchId(null)
						closeDialog()
						onSuccess()
					}
				}
			} catch (e) {
				console.error("Batch polling failed:", e)
			}
		}, 1000)

		return () => {
			isMounted = false
			clearInterval(intervalId)
		}
	}, [batchId, xhr, closeDialog, onSuccess])

	const openFilter = () => {
		closeDialog()
		openAdvancedFilters()
	}

	const toggleItem = (id: number) => {
		setSelectedIds((ids) =>
			ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]
		)
	}

	const isItemSelected = (id: number) =>
		isSelectAll ? !selectedIds.includes(id) : selectedIds.includes(id)

	return (
		<>
			{children({ onAssign: () => openDialog() })}
			<Dialog
				open={isOpen}
				onClose={closeDialog}
				title="Bulk Assign Trip Plan Requests"
				lg
			>
				<Form<{ confirmed_count: number; sales_owner_id: IUser }>
					validate={validateFormValues(
						Validator.object().shape({
							confirmed_count: Validator.number()
								.equals(
									[selectedCount],
									`Please type ${selectedCount} to confirm the selection`
								)
								.not([0], "Please type a valid number")
								.required(
									`Please type ${selectedCount} to confirm the selection`
								),
							sales_owner_id: Validator.object().required(
								"Please select a sales person to assign"
							),
						})
					)}
					onSubmit={withServerErrors(async ({ sales_owner_id }) => {
						const resp = await xhr.post(
							"/trip-plan-request-owners/bulk-assign",
							{
								...params,
								owner_id: sales_owner_id.id,
								trip_requests_count: selectedCount,
								...(isSelectAll
									? {
											excluded_ids: selectedIds,
										}
									: {
											included_ids: selectedIds,
										}),
							}
						)
						setBatchId(resp.data.data.id)
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body padding="8">
								<Inline justifyContent={"between"} gap={"2"}>
									<Text fontSize="lg" fontWeight="semibold">
										Please review details
										{areFiltersApplied ? ` (${totalCount} total)` : null}
									</Text>
									{!isSelectAll && areFiltersApplied && items.length > 0 ? (
										<Inline
											cursor="pointer"
											as="label"
											alignItems="center"
											gap="2"
										>
											<CheckboxInput
												checked={isSelectAll}
												onChange={() => {
													setIsSelectAll(true)
													setSelectedIds([])
												}}
												name="select_all"
												id="select_all_tpr"
											/>
											<Box htmlFor="select_all_tpr" as="label">
												Select All
											</Box>
										</Inline>
									) : null}
								</Inline>

								<Stack
									padding="4"
									borderWidth="1"
									borderColor="warning"
									rounded="lg"
									marginY="4"
								>
									<Box style={{ height: "300px" }} overflowY="auto" as="div">
										{!areFiltersApplied ? (
											<Box
												display="flex"
												alignItems="center"
												justifyContent="center"
												flexDirection="col"
												gap="4"
												style={{ height: "100%" }}
											>
												<Box as="p">
													Please choose right filter or search option to assign
												</Box>
												<Button onClick={() => openFilter()} type="button">
													Close and Apply Filters
												</Button>
											</Box>
										) : !items.length && !isLoading ? (
											<Box
												display="flex"
												alignItems="center"
												justifyContent="center"
												style={{ height: "100%" }}
											>
												<Text color="muted">
													No trip plan requests found for the current filters.
												</Text>
											</Box>
										) : (
											<Table
												bordered
												responsive
												hover
												headers={[" ", "Name", "Contact", "Details"]}
												rows={items.map((r) => [
													<CheckboxInput
														key={r.id}
														id={`tpr_${r.id}`}
														checked={isItemSelected(r.id)}
														onChange={() => toggleItem(r.id)}
													/>,
													<Stack key={`name_${r.id}`} gap="px">
														<Text fontWeight="semibold">{r.name}</Text>
														{r.trip_source ? (
															<Text fontSize="sm" color="muted">
																{r.trip_source}
															</Text>
														) : null}
													</Stack>,
													<Stack key={`contact_${r.id}`} gap="px">
														{r.phone_number ? (
															<PhoneNumber value={r.phone_number} />
														) : null}
														{r.email ? <Email value={r.email} /> : null}
													</Stack>,
													<Stack key={`details_${r.id}`} gap="px">
														{r.destination ? (
															<Text>{r.destination}</Text>
														) : null}
														{r.start_date ? (
															<Text fontSize="sm" color="muted">
																{r.start_date}
																{r.no_of_days ? ` • ${r.no_of_days}D` : null}
															</Text>
														) : null}
														{r.owner ? (
															<Text fontSize="sm" color="muted">
																<Icons.User /> {r.owner.name}
															</Text>
														) : null}
													</Stack>,
												])}
											/>
										)}
										{isLoading ? (
											<Box textAlign="center" paddingY="4">
												<Spinner />
											</Box>
										) : null}
										{hasMore.current && !isLoading ? (
											<Box textAlign="center" paddingY="2">
												<Button
													onClick={() => fetchRequests(currentPage + 1)}
													level="tertiary"
													size="sm"
												>
													Load More
												</Button>
											</Box>
										) : null}
									</Box>
								</Stack>

								{areFiltersApplied && items.length > 0 ? (
									<Inline gap={"4"} flexWrap={"wrap"}>
										<SelectField
											name="sales_owner_id"
											select={SelectUsers}
											label="Choose Sales Person"
											fetchOnMount
										/>
										{selectedCount > 0 ? (
											<TextInputField
												label={`Please type ${selectedCount} to confirm`}
												name="confirmed_count"
												type="number"
												aria-label="Enter the count to confirm assignment"
												required
											/>
										) : null}
									</Inline>
								) : null}
							</Dialog.Body>
							{areFiltersApplied && items.length > 0 ? (
								<Dialog.Footer>
									<Stack gap={"4"}>
										<SubmissionError />
										<Inline gap={"2"}>
											<Button
												disabled={submitting || !selectedCount || !!batchId}
												type="submit"
												level="primary"
											>
												{submitting || !!batchId
													? "Assigning..."
													: `Assign (${selectedCount})`}
											</Button>
											<Button
												disabled={submitting || !!batchId}
												onClick={() => closeDialog()}
												level="tertiary"
											>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</Dialog.Footer>
							) : null}
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}
